/**
 * monedas.tsx — las monedas, traídas una vez.
 *
 * Vive en un contexto por el mismo motivo que el catálogo: lo necesitan cinco
 * pantallas —contrapartes, por pagar, por cobrar, configuración y la consola—
 * y pedirlo en cada una haría cinco llamadas por el mismo dato. Peor: dejaría
 * que dos pantallas abiertas ofrezcan listas distintas después de que el hub
 * replique una moneda nueva.
 *
 * ── Se descarta al cambiar de empresa ─────────────────────────────────────
 *
 * `operables` depende de la empresa activa: su primera fila ES la moneda local.
 * Sin descartarlo, cambiar de empresa dejaría en pantalla la lista de la
 * anterior, con la moneda local de otro rotulada como propia.
 *
 * `conocidas` no depende de ninguna —es el maestro de tipos de cambio y las
 * monedas locales que ya existen— y por eso se pide aunque no haya empresa: la
 * consola del operador da de alta empresas y todavía no tiene una.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { useSesion } from './sesion.tsx';
import {
  traerMonedasConocidas, traerMonedasOperables,
  type MonedaConocida, type MonedaOperable,
} from '../datos/libros.ts';

interface Contexto {
  readonly operables: readonly MonedaOperable[];
  readonly conocidas: readonly MonedaConocida[];
  readonly cargando: boolean;
  readonly recargar: () => void;
}

const Ctx = createContext<Contexto | null>(null);

export function ProveedorDeMonedas({ children }: { children: ReactNode }) {
  const { pedir, sesion } = useSesion();
  const empresa = sesion?.empresa.id ?? null;

  const [operables, setOperables] = useState<readonly MonedaOperable[]>([]);
  const [conocidas, setConocidas] = useState<readonly MonedaConocida[]>([]);
  const [cargando, setCargando] = useState(false);
  const [vuelta, setVuelta] = useState(0);

  const recargar = useCallback(() => { setVuelta((n) => n + 1); }, []);

  useEffect(() => {
    let vivo = true;
    setCargando(true);

    /* ▶ Las dos en paralelo y cada una con su propio `catch`.
       Sin sesión abierta las dos fallan con 401 y eso NO es un fallo de esta
       pantalla: es que todavía no hay a quién preguntarle. Se deja la lista
       vacía y el desplegable enseña sólo «sin declarar» — que es cierto.

       Y una que falle no puede llevarse a la otra: la consola del operador
       necesita `conocidas` y no tiene empresa, así que `operables` le contesta
       SB024 siempre. Con un solo `catch` eso vaciaría también la que sí sirve. */
    void Promise.allSettled([
      empresa === null
        ? Promise.resolve([] as readonly MonedaOperable[])
        : traerMonedasOperables(pedir),
      traerMonedasConocidas(pedir),
    ]).then(([op, co]) => {
      if (!vivo) return;
      setOperables(op.status === 'fulfilled' ? op.value : []);
      setConocidas(co.status === 'fulfilled' ? co.value : []);
      setCargando(false);
    });

    return () => { vivo = false; };
  }, [pedir, empresa, vuelta]);

  const valor = useMemo(
    () => ({ operables, conocidas, cargando, recargar }),
    [operables, conocidas, cargando, recargar],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useMonedas(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useMonedas fuera del ProveedorDeMonedas');
  return c;
}
